import { ref, computed, onMounted, watch } from 'vue'
import GoogleMapsWithMarkers from '../../../../Frontend/src/components/GoogleMapsWithMarkers/GoogleMapsWithMarkers'
import MapSuggestions from './components/MapSuggestions.vue'
import SelectedBank from './components/SelectedBank.vue'
import setFocusPoints from '../../../FindCenter/Blocks/FindMap/methods/setFocusPoints'
import zoomMapToCenters from "../../../FindCenter/Blocks/FindMap/methods/zoomMapToCenters"
import getDistanceFromLatLonInKm from "../../../FindCenter/Blocks/FindMap/methods/getDistanceFromLatLonInKm"
import initGooglePlacesAutocomplete from "../../../FindCenter/Blocks/FindMap/methods/initGooglePlacesAutocomplete"
import ResultMessage from './components/ResultMessage.vue'
import { getMaxLocationsShownLength, checkIfNoDirectResults, getFilteredList } from './methods/filial-helper'
import GTMregisterTotalkredit from './methods/GTMregisterTotalkredit'

export default {
    name: "PIMap",
    components: {
        GoogleMapsWithMarkers,
        MapSuggestions,
        SelectedBank,
        ResultMessage
    },
    props: {
        mapkey: {
            type: String,
            required: true,
        },
        labels: {
            type: Object,
            required: true,
        },
        isBusiness: {
            type: Boolean,
            required: true,
        },
        startLatitude: {
            type: Number,
            required: true,
        },
        startLongitude: {
            type: Number,
            required: true,
        },
        availableBanks: {
            type: Array,
            required: false,
        },
        centerInformation: {
          type: Array,
          required: true,
        }
    },
    setup(props) {
        const currentZoom = ref(7)
        const placeSelected = ref(null)
        const focusPoints = ref([]) // map will zoom to these focus points
        const showMapsMobile = ref(true)
        const centerInformationComplete = ref(props.centerInformation)
        const centerInformationFiltered = ref([]) // map will display these locations
        const showListMobileCount = ref(1000)
        const selectedBank = ref(null)
        const bankId = ref(null)
        const availableBankSelected = ref(null)
        const availableBankRef = ref(null)
        const noDirectResults = ref(false)

        const googlePlacesSearchResultsRef = ref(null)
        const googlePlacesSearchField = ref(null)
        const googlePlacesSearchResultsShown = ref(false)
        
        const updateCurrentZoom = (zoom) => currentZoom.value = zoom
        const handleShowMapMobile = () => showMapsMobile.value = true
        const handleShowListResultsMobile = () => showMapsMobile.value = false
        const handleShowAllListMobile = () => showListMobileCount.value = 10000

        // handling google places search results, to handle WCAG
        const googlePlacesSearchResultsMutationHandler = (mutationsList) => {
            for (const mutation of mutationsList) {
                if (mutation.type === 'attributes') {
                    googlePlacesSearchResultsShown.value = googlePlacesSearchResultsRef.value.style.display !== 'none'

                    if (mutation.attributeName === 'class' && mutation.target.classList.value.includes('pac-item-selected')) {
                        mutation.target.setAttribute('aria-selected', 'true')
                    }
                }

                if (mutation.type === 'childList') {
                    mutation.addedNodes.forEach((item) => {
                        item.setAttribute('aria-selected', 'false')
                        observer.observe(item, { attributes: true })
                    })
                }
            }
        }

        const observer = new MutationObserver(googlePlacesSearchResultsMutationHandler)

        const findGooglePlacesSearchResultsElement = () => {
            const searchResults = document.querySelector('.pac-container')
            if (!searchResults) {
                setTimeout(findGooglePlacesSearchResultsElement, 100)
                return
            }
            googlePlacesSearchResultsRef.value = searchResults
            googlePlacesSearchResultsRef.value.setAttribute('role', 'listbox')
            googlePlacesSearchResultsRef.value.setAttribute('id', 'googlePlacesSearchResults')
            observer.observe(googlePlacesSearchResultsRef.value, { attributes: true, childList: true })
        }

        onMounted(() => {
            initGooglePlacesAutocomplete(googlePlacesSearchField.value, filterCentersAndZoom)
            findGooglePlacesSearchResultsElement()
        })

        // controlling focus points and pins data
        const filterCentersAndZoom = function (places) {
            showListMobileCount.value = 1000
            const searchHasHits = !!places.getPlace().geometry
            if (searchHasHits) {
                placeSelected.value = places.getPlace()
                handleUpdateFilteredInformationFromGoogleSearch(placeSelected.value)
            } else {
              placeSelected.value = null
            }
        }

        const handleUpdateFilteredInformationFromGoogleSearch = (place) => {
            const maxNumberOfLocationsShown = getMaxLocationsShownLength(googlePlacesSearchField.value.value, availableBankSelected.value?.name, centerInformationComplete.value)
            const googleSearchablePlaces = availableBankSelected.value?.name ? centerInformationComplete.value.filter(center => center.bankName === availableBankSelected.value?.name) : centerInformationComplete.value
            centerInformationFiltered.value = zoomMapToCenters(place, googleSearchablePlaces)
        }

        const showFilteredResults = computed(() => {
            let show = false
            if (centerInformationFiltered.value && centerInformationFiltered.value.length > 0 || selectedBank.value || availableBankSelected.value?.external || noDirectResults.value) {
                show = true
            }
            return show
        })

        const handleSearch = () => {                
            var request = {
                input: googlePlacesSearchField.value.value,
                sessionToken: new google.maps.places.AutocompleteSessionToken()
            };

            var service = new google.maps.places.AutocompleteService();
            service.getPlacePredictions(request, function(predictions, status) {
                if (status === google.maps.places.PlacesServiceStatus.OK) {
                    // if any predictions we use the google api service get a place object similar to the one you get when using the google dropdown
                    if (predictions.length > 0) {
                        getPlaceDetailsFromGoogleAPI(predictions[0].place_id)
                    }
                }
            });
        }

        function getPlaceDetailsFromGoogleAPI(placeId) {
            var request = {
                placeId: placeId,
                fields: ['name', 'geometry', 'formatted_address', 'place_id'] // Specify the fields you need
            };

            var service = new google.maps.places.PlacesService(document.createElement('div'));
            service.getDetails(request, function(place, status) {
                if (status === google.maps.places.PlacesServiceStatus.OK) {
                    handleUpdateFilteredInformationFromGoogleSearch(place)
                }
            });
        }

        const handleCenterClick = (center) => {
            selectedBank.value = center
            showMapsMobile.value = false
            noDirectResults.value = false
            focusPoints.value = setFocusPoints([center])
        }

        const unSelectBank = () => {
            selectedBank.value = null
            focusPoints.value = setFocusPoints(centerInformationFiltered.value)
        }

        const unSelectAvailableBank = async () => {
            availableBankSelected.value = null
            bankId.value = null
            availableBankRef.value.value = props.availableBanks[0].value
        }

        const handleBankChange = async (data) => {
            const id = parseFloat(data.target.value)

            function handleFilteringResults() {
                if (googlePlacesSearchField.value.value !== "") {
                    handleSearch()
                } else {
                    centerInformationFiltered.value = getFilteredList(googlePlacesSearchField.value.value, availableBankSelected.value?.name, centerInformationComplete.value)
                }
            }
            
            if (id === -1) {
                availableBankSelected.value = null
                bankId.value = null
                handleFilteringResults()
                return 
            }

            bankId.value = id
            const bank = props.availableBanks.find(b => b.value === id)
            availableBankSelected.value = bank
            handleFilteringResults()
        }

        // Reset helpers
        const resetFilial = () => availableBankRef.value.value = props.availableBanks[0].value
        const resetSearchInput = () => googlePlacesSearchField.value.value = ""
        const resetSelectedBank = () => selectedBank.value = null
        const resetAvailableBank = () => { availableBankSelected.value = null; bankId.value = null }
        const resetFilteredCenterInformation = () => centerInformationFiltered.value = []

        const resetSearch = () => {
            resetSearchInput()
            centerInformationFiltered.value = getFilteredList(googlePlacesSearchField.value.value, availableBankSelected.value?.name, centerInformationComplete.value)
        }

        const resetFilialFilter = () => {
            resetFilial()
            resetSelectedBank()
            resetAvailableBank()
            resetFilteredCenterInformation()
        }

        const resetAll = () => {
            resetFilial()
            resetSearchInput()
            resetSelectedBank()
            resetAvailableBank()
            resetFilteredCenterInformation()
            noDirectResults.value = false
        }

        watch(() => centerInformationFiltered.value, (newValue) => {
            noDirectResults.value = checkIfNoDirectResults(googlePlacesSearchField.value.value, availableBankSelected.value?.name, centerInformationComplete.value)
            if (newValue.length > 0) {
                focusPoints.value = setFocusPoints(centerInformationFiltered.value)
                noDirectResults.value = false
            } else {
                focusPoints.value = setFocusPoints(centerInformationComplete.value)
            }
        })

        const trackEvent = (eventAction, piName, isExternal) => {
            GTMregisterTotalkredit(
                'find rådgiver',
                eventAction, 
                piName, 
                isExternal ? "udenfor samarbejdet" : "indenfor samarbejdet", 
                piName
            )
        }

        return {
            focusPoints,
            currentZoom,
            showMapsMobile,
            centerInformationFiltered,
            showListMobileCount,
            showFilteredResults,
            selectedBank,
            googlePlacesSearchField,
            availableBankSelected,
            availableBankRef,
            googlePlacesSearchResultsShown,
            noDirectResults,
            updateCurrentZoom,
            handleShowMapMobile,
            handleShowListResultsMobile,
            handleShowAllListMobile,
            handleCenterClick,
            unSelectBank,
            unSelectAvailableBank,
            handleBankChange,
            resetSearch,
            resetFilialFilter,
            resetAll,
            handleSearch,
            trackEvent
        }
    }
}